import {
  ActionIcon,
  Badge,
  createStyles,
  Divider,
  Group,
  Stack,
  Text,
  Box,
} from '@mantine/core';
import dayjs from 'dayjs';
import { AnimatePresence, motion } from 'framer-motion';
import React from 'react';

import { ReactComponent as CollapseRight } from '@portals/icons/linear/collapse-right.svg';
import { ReactComponent as QuestionCircle } from '@portals/icons/linear/question-circle.svg';
import { CurrencyCode, InvoiceItem, PaymentIntervalEnum } from '@portals/types';
import { formatCurrency } from '@portals/utils';

import { InvoiceItemAmountPreviewTooltip } from './preview-tooltip/InvoiceItemAmountPreviewTooltip';
import { InvoiceItemPreviewTooltip } from './preview-tooltip/InvoiceItemPreviewTooltip';
import { ProductImage } from '../ProductImage';

interface InvoiceItemsPanelProps {
  invoiceItems: Array<InvoiceItem>;
  itemsCount: number;
  isOpen: boolean;
  currency: CurrencyCode;
  onClose: () => void;
}

export function InvoiceItemsPanel({
  invoiceItems,
  isOpen,
  onClose,
  currency,
  itemsCount,
}: InvoiceItemsPanelProps) {
  const { classes, theme } = useStyles();

  const getPeriodDate = (date: string) => {
    return dayjs(date).format('MMMM D');
  };

  const getInterval = (paymentInterval: PaymentIntervalEnum) => {
    if (paymentInterval === 'monthly') return ' / month';
    else if (paymentInterval === 'yearly') return ' / year';

    return '';
  };

  return (
    <>
      <AnimatePresence initial={false}>
        {isOpen && (
          <motion.div
            initial={{ x: '100%' }}
            animate={{ x: 0 }}
            exit={{ x: '100%' }}
            transition={{ type: 'linear' }}
            className={classes.wrapper}
          >
            <Stack spacing="xxl">
              <Group position="apart">
                <Text size="md">Items ({itemsCount})</Text>

                <ActionIcon variant="subtle" onClick={onClose}>
                  <CollapseRight width={18} height={18} />
                </ActionIcon>
              </Group>

              <Stack>
                {invoiceItems.map((invoiceItem) => (
                  <Stack key={invoiceItem.id}>
                    <Box className={classes.rowItem}>
                      <InvoiceItemPreviewTooltip item={invoiceItem}>
                        <Group
                          noWrap
                          spacing="xl"
                          className={classes.textOverflow}
                        >
                          <ProductImage
                            src={invoiceItem.product_image_url}
                            width={48}
                            className={classes.imageWrapper}
                          />

                          <Stack
                            h="100%"
                            spacing="xs"
                            className={classes.textOverflow}
                          >
                            <Text weight={600} color="blue_gray.9" truncate>
                              {invoiceItem.product_name}
                            </Text>

                            <Group>
                              <Badge
                                size="lg"
                                color="gray"
                                radius="md"
                                variant="light"
                              >
                                {invoiceItem.quantity}x
                              </Badge>
                            </Group>
                          </Stack>
                        </Group>
                      </InvoiceItemPreviewTooltip>

                      <Stack spacing={5}>
                        <Text weight={600}>Price</Text>
                        <Text color="gray.5">
                          {formatCurrency(invoiceItem.price_in_scu, currency)}
                          {getInterval(invoiceItem.payment_interval)}
                        </Text>
                      </Stack>

                      <Stack spacing={5}>
                        <Text weight={600}>Period</Text>
                        {invoiceItem.paid_period.to && (
                          <Text color="gray.5">
                            {getPeriodDate(invoiceItem.paid_period.from)} -{' '}
                            {getPeriodDate(invoiceItem.paid_period.to)}
                          </Text>
                        )}
                      </Stack>

                      <Stack spacing={5}>
                        <Group spacing="xs">
                          <Text weight={600}>Charge amount</Text>
                          <InvoiceItemAmountPreviewTooltip
                            item={invoiceItem}
                            currency={currency}
                          >
                            <QuestionCircle
                              width={16}
                              height={16}
                              color={theme.colors.gray[5]}
                            />
                          </InvoiceItemAmountPreviewTooltip>
                        </Group>
                        <Text color="gray.5">
                          {formatCurrency(
                            invoiceItem.prorated_price_in_scu,
                            currency
                          )}
                        </Text>
                      </Stack>
                    </Box>
                    <Divider />
                  </Stack>
                ))}
              </Stack>
            </Stack>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
}

const useStyles = createStyles((theme) => ({
  wrapper: {
    position: 'fixed',
    backgroundColor: theme.white,
    top: theme.other.navbarHeight,
    right: 400,
    width: 900,
    height: `calc(100vh - ${theme.other.navbarHeight}px)`,
    boxShadow: '6px 0px 41px 0px rgba(0, 0, 0, 0.13)',
    clipPath: 'inset(0 0 0 -41px)',
    padding: theme.spacing.xxl,
  },

  textOverflow: {
    overflow: 'hidden',
  },

  rowItem: {
    display: 'grid',
    gap: theme.spacing.xl,
    gridTemplateColumns: '2fr',
    gridAutoFlow: 'column',
    gridAutoColumns: '1fr',
  },

  imageWrapper: {
    overflow: 'hidden',
    border: `1px solid ${theme.colors.gray[1]}`,
    borderRadius: theme.radius.lg,
  },
}));
